const normaliseLeaseStatus = (value) => String(value || '').trim().toLowerCase().replace(/\s+/g, '_');

export const CONTRACTUAL_LEASE_STATUSES = Object.freeze(['active', 'renewed']);
export const HISTORICAL_LEASE_STATUSES = Object.freeze(['expired', 'terminated', 'archived']);

export const toLeaseDayStamp = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return Number.isNaN(date.getTime()) ? null : date.getTime();
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
};

export const isContractualLease = (lease) => {
  const status = normaliseLeaseStatus(lease?.status);
  if (CONTRACTUAL_LEASE_STATUSES.includes(status)) return true;
  if (status !== 'draft') return false;

  // Backward compatibility: older Quick Add saved complete future leases as
  // "draft" solely because their start date had not arrived. Those records
  // still represent a tenant/property/term/rent commitment and must transition
  // correctly once their dates become effective.
  const start = toLeaseDayStamp(lease?.startDate ?? lease?.leaseStart);
  const end = toLeaseDayStamp(lease?.endDate ?? lease?.leaseEnd);
  const hasParties = Boolean(lease?.tenantId || lease?.tenant) && Boolean(lease?.propertyId || lease?.property);
  const rent = Number(lease?.weeklyRent ?? lease?.rentAmount);
  return hasParties && start !== null && end !== null && end >= start && Number.isFinite(rent) && rent > 0;
};

export const isLeaseEffectiveOn = (lease, asOf = Date.now()) => {
  if (!lease || !isContractualLease(lease)) return false;
  const start = toLeaseDayStamp(lease.startDate ?? lease.leaseStart);
  const end = toLeaseDayStamp(lease.endDate ?? lease.leaseEnd);
  const target = toLeaseDayStamp(asOf);
  if (start === null || end === null || target === null) return false;
  return start <= target && target <= end;
};

export const isFutureContractedLease = (lease, asOf = Date.now()) => {
  if (!lease || !isContractualLease(lease)) return false;
  const start = toLeaseDayStamp(lease.startDate ?? lease.leaseStart);
  const target = toLeaseDayStamp(asOf);
  return start !== null && target !== null && start > target;
};

const byStartAscending = (a, b) => {
  const aStart = toLeaseDayStamp(a?.startDate ?? a?.leaseStart) ?? Number.MAX_SAFE_INTEGER;
  const bStart = toLeaseDayStamp(b?.startDate ?? b?.leaseStart) ?? Number.MAX_SAFE_INTEGER;
  if (aStart !== bStart) return aStart - bStart;
  return String(a?.id || '').localeCompare(String(b?.id || ''));
};

const byStartDescending = (a, b) => -byStartAscending(a, b);

export const selectAuthoritativeCurrentLeases = (leases = [], asOf = Date.now()) => {
  const current = (leases || []).filter(lease => isLeaseEffectiveOn(lease, asOf));
  if (!current.length) return [];

  const wholeProperty = current.filter(lease => !String(lease?.spaceId || lease?.space || '').trim());
  if (wholeProperty.length) {
    return [wholeProperty.sort(byStartDescending)[0]];
  }

  const bySpace = new Map();
  current.sort(byStartDescending).forEach(lease => {
    const spaceId = String(lease?.spaceId || lease?.space || '').trim();
    if (!spaceId || bySpace.has(spaceId)) return;
    bySpace.set(spaceId, lease);
  });
  return [...bySpace.values()].sort(byStartAscending);
};

export const getLeasePosition = (leases = [], asOf = Date.now()) => {
  const contractual = (leases || []).filter(isContractualLease);
  const current = contractual.filter(lease => isLeaseEffectiveOn(lease, asOf)).sort(byStartDescending);
  const future = contractual.filter(lease => isFutureContractedLease(lease, asOf)).sort(byStartAscending);
  const target = toLeaseDayStamp(asOf);

  // Historical records remain visible after expiry/termination, but they are
  // deliberately excluded from current/future occupancy and rent authority.
  const historicalCandidates = (leases || []).filter(lease => {
    if (isContractualLease(lease)) return true;
    const status = normaliseLeaseStatus(lease?.status);
    if (!HISTORICAL_LEASE_STATUSES.includes(status)) return false;
    const start = toLeaseDayStamp(lease?.startDate ?? lease?.leaseStart);
    const end = toLeaseDayStamp(lease?.endDate ?? lease?.leaseEnd);
    return start !== null && end !== null && end >= start;
  });
  const previous = historicalCandidates
    .filter(lease => {
      const end = toLeaseDayStamp(lease?.endDate ?? lease?.leaseEnd);
      return end !== null && target !== null && end < target;
    })
    .sort((a, b) => {
      const aEnd = toLeaseDayStamp(a?.endDate ?? a?.leaseEnd) ?? 0;
      const bEnd = toLeaseDayStamp(b?.endDate ?? b?.leaseEnd) ?? 0;
      return bEnd - aEnd;
    });

  return {
    current: current[0] || null,
    currentLeases: current,
    next: future[0] || null,
    futureLeases: future,
    previous: previous[0] || null,
    previousLeases: previous
  };
};

export const getNextContractedChangeDate = (leases = [], asOf = Date.now()) => {
  const target = toLeaseDayStamp(asOf);
  if (target === null) return null;
  let next = null;
  (leases || []).forEach(lease => {
    if (!isContractualLease(lease)) return;
    const start = toLeaseDayStamp(lease?.startDate ?? lease?.leaseStart);
    if (start !== null && start > target && (next === null || start < next)) next = start;
    const leaseStart = toLeaseDayStamp(lease?.startDate ?? lease?.leaseStart);
    const leaseEnd = toLeaseDayStamp(lease?.endDate ?? lease?.leaseEnd);
    const history = Array.isArray(lease?.rentIncreaseHistory) ? lease.rentIncreaseHistory : [];
    history.forEach(increase => {
      const effective = toLeaseDayStamp(increase?.effectiveDate);
      const withinLease = effective !== null
        && leaseStart !== null
        && leaseEnd !== null
        && leaseStart <= effective
        && effective <= leaseEnd;
      if (withinLease && effective > target && (next === null || effective < next)) next = effective;
    });
  });
  return next;
};

export default {
  CONTRACTUAL_LEASE_STATUSES,
  HISTORICAL_LEASE_STATUSES,
  toLeaseDayStamp,
  isContractualLease,
  isLeaseEffectiveOn,
  isFutureContractedLease,
  selectAuthoritativeCurrentLeases,
  getLeasePosition,
  getNextContractedChangeDate
};